import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const SearchResult: React.FC = () => {
    const [searchParams] = useSearchParams();
    const keyword = searchParams.get('keyword') || '';
    const [results, setResults] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // 清理过期的搜索缓存
    const cleanupExpiredCache = () => {
        const keys = Object.keys(localStorage);
        const searchKeys = keys.filter(key => key.startsWith('search_'));
        const now = Date.now();
        const maxAge = 10 * 60 * 1000; // 10分钟过期时间

        searchKeys.forEach(key => {
            const timeKey = `${key}_time`;
            const timeStr = localStorage.getItem(timeKey);
            if (timeStr) {
                const cacheAge = now - parseInt(timeStr);
                if (cacheAge > maxAge) {
                    localStorage.removeItem(key);
                    localStorage.removeItem(timeKey);
                }
            }
        });
    };

    // 新增：用于动画切换图片
    const [hoveredImgIdx, setHoveredImgIdx] = useState<{ [key: number]: number }>({});
    const [, setHoveredIdx] = useState<number | null>(null);

    // 统一判断可见性的工具：兼容 boolean、字符串等
    const isVisible = (val: any): boolean => {
        if (typeof val === 'boolean') return val === true;
        if (typeof val === 'string') return val.toLowerCase() === 'true' || val === '1';
        if (typeof val === 'number') return val === 1;
        return false;
    };

    useEffect(() => {
        // 清理过期缓存
        cleanupExpiredCache();

        const fetchResults = async () => {
            if (!keyword) {
                setResults([]);
                return;
            }

            setLoading(true);

            // 1. 先从 localStorage 读取
            const cacheKey = `search_${keyword.toLowerCase()}`;
            const cached = localStorage.getItem(cacheKey);
            const cacheTime = localStorage.getItem(`${cacheKey}_time`);
            const now = Date.now();
            const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
            const maxAge = 2 * 60 * 1000; // 2分钟缓存

            if (cached && cacheAge < maxAge) {
                try {
                    const searchResults = JSON.parse(cached);
                    const sanitized = Array.isArray(searchResults)
                        ? searchResults
                            .filter((p: any) => isVisible(p?.visible))
                            .sort((a: any, b: any) => (a.title || '').localeCompare(b.title || ''))
                        : [];
                    setResults(sanitized);
                    setLoading(false);
                    return;
                } catch { }
            }

            // 2. 检查全局产品缓存
            const globalCacheKey = 'all_products';
            const globalCached = localStorage.getItem(globalCacheKey);
            const globalCacheTime = localStorage.getItem(`${globalCacheKey}_time`);
            const globalCacheAge = globalCacheTime ? now - parseInt(globalCacheTime) : 0;
            const globalMaxAge = 5 * 60 * 1000; // 5分钟全局产品缓存

            let all: any[] = [];

            if (globalCached && globalCacheAge < globalMaxAge) {
                try {
                    const parsed = JSON.parse(globalCached);
                    all = Array.isArray(parsed) ? parsed.filter((p: any) => isVisible(p?.visible)) : [];
                } catch {
                    // 如果全局缓存失败，重新获取
                    const snapshot = await getDocs(query(collection(db, 'products'), where('visible', '==', true)));
                    all = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                    // 更新全局缓存
                    localStorage.setItem(globalCacheKey, JSON.stringify(all));
                    localStorage.setItem(`${globalCacheKey}_time`, Date.now().toString());
                }
            } else {
                // 重新获取所有产品
                const snapshot = await getDocs(query(collection(db, 'products'), where('visible', '==', true)));
                all = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // 更新全局缓存
                localStorage.setItem(globalCacheKey, JSON.stringify(all));
                localStorage.setItem(`${globalCacheKey}_time`, Date.now().toString());
            }

            const filtered = all
                .filter((p: any) =>
                    isVisible(p?.visible) && p.title?.toLowerCase().includes(keyword.toLowerCase())
                )
                .sort((a: any, b: any) => (a.title || '').localeCompare(b.title || ''));
            setResults(filtered);

            // 3. 缓存搜索结果
            localStorage.setItem(cacheKey, JSON.stringify(filtered));
            localStorage.setItem(`${cacheKey}_time`, Date.now().toString());

            setLoading(false);
        };
        fetchResults();
    }, [keyword]);

    const visibleResults = results
        .filter((p: any) => isVisible(p?.visible))
        .sort((a: any, b: any) => (a.title || '').localeCompare(b.title || ''));

    return (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 32 }}>
            <h2 style={{ fontWeight: 700, fontSize: 24, marginBottom: 24 }}>
                Search Results for "{keyword}"
            </h2>
            {loading ? <div>Loading...</div> : (
                visibleResults.length === 0 ? (
                    <div style={{ color: '#888' }}>No results found.</div>
                ) : (
                    <div className="product-grid">
                        {visibleResults.map((product, idx) => {
                            const variantImages = product.variants?.map((v: any) => v.image).filter(Boolean) || [];
                            const allImages = [...variantImages, ...(product.images || [])];
                            const hasSecondImg = allImages.length > 1;
                            const showImgIdx = hoveredImgIdx[idx] ?? 0;

                            return (
                                <div
                                    className="product-card"
                                    key={product.id}
                                    onClick={() => navigate(`/product/${product.id}`)}
                                    style={{
                                        cursor: 'pointer',
                                        height: 300,
                                        padding: 16,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start'
                                    }}
                                    onMouseEnter={() => {
                                        if (hasSecondImg) {
                                            setTimeout(() => {
                                                setHoveredImgIdx(prev => ({ ...prev, [idx]: 1 }));
                                            }, 200); // 200ms后切换
                                        }
                                        setHoveredIdx(idx);
                                    }}
                                    onMouseLeave={() => {
                                        setHoveredImgIdx(prev => ({ ...prev, [idx]: 0 }));
                                        setHoveredIdx(null);
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 180,
                                            height: 180,
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            margin: '0 auto 8px auto',
                                            background: '#f5f5f5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}
                                    >
                                        {/* 第一张图片 */}
                                        <img
                                            loading="lazy"
                                            src={allImages[0] || 'https://via.placeholder.com/150'}
                                            alt={product.title}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                display: 'block',
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                opacity: showImgIdx === 0 ? 1 : 0,
                                                transition: 'opacity 0.5s cubic-bezier(.4,0,.2,1)'
                                            }}
                                        />
                                        {/* 第二张图片（如果有） */}
                                        {allImages[1] && (
                                            <img
                                                src={allImages[1]}
                                                alt={product.title}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    opacity: showImgIdx === 1 ? 1 : 0,
                                                    transition: 'opacity 0.5s cubic-bezier(.4,0,.2,1)'
                                                }}
                                            />
                                        )}
                                    </div>
                                    <p style={{ fontWeight: 500, margin: '8px 0 4px 0', color: '#222' }}>{product.title}</p>
                                    {product.variants && product.variants.length > 0 && (
                                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', margin: '4px 0 8px 0' }}>
                                            {product.variants.map((variant: any, vIdx: number) => (
                                                <span
                                                    key={variant.colorId || vIdx}
                                                    title={variant.colorCode}
                                                    style={{
                                                        display: 'inline-block',
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: '50%',
                                                        background: variant.colorCode,
                                                        border: '1.5px solid #eee',
                                                        boxSizing: 'border-box',
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    <p style={{ color: '#27ae60', fontWeight: 600 }}>
                                        ${product.price}
                                    </p>
                                    {/* 可选：库存状态 */}
                                    {product.variants && product.variants.length > 0 ? (
                                        product.variants.some((v: any) => typeof v.stock === 'number' && v.stock > 0) ? (
                                            <p style={{ color: '#27ae60', fontSize: 14 }}>Available</p>
                                        ) : (
                                            <p style={{ color: '#e74c3c', fontSize: 14 }}>Out of Stock</p>
                                        )
                                    ) : (
                                        String(product.availableNum) === '0' ? (
                                            <p style={{ color: '#e74c3c', fontSize: 14 }}>Out of Stock</p>
                                        ) : (
                                            <p style={{ color: '#888', fontSize: 14 }}>{product.availableNum ?? ''} Available</p>
                                        )
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )
            )}
        </div>
    );
};

export default SearchResult;